import { useState, useEffect } from 'react';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay, rectIntersection } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from '../components/SortableItem';
import { DroppableFolder } from '../components/DroppableFolder';
import { DraggableContentItem } from '../components/DraggableContentItem';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, FileText, Music, Bell, MessageSquare, CreditCard, Video, ExternalLink, Eye, Calendar, Headphones, Pin, UserCircle, TrendingUp, DollarSign, RefreshCw, AlertCircle, Flag, Shield, ShieldOff, AlertTriangle, CheckCircle, XCircle, FolderPlus, Folder, FolderOpen, Move, GripVertical } from 'lucide-react';
import GoogleDrivePreview from '../components/GoogleDrivePreview';
import MediaViewer from '../components/MediaViewer';
import { isGoogleDriveUrl, extractGoogleDriveFileId } from '../utils/googleDrive';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [activeTab, setActiveTab] = useState('lessons');
  const [lessons, setLessons] = useState([]);
  const [content, setContent] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [communityUsers, setCommunityUsers] = useState([]);
  const [communityFilter, setCommunityFilter] = useState('all');
  const [flaggedPosts, setFlaggedPosts] = useState([]);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [payments, setPayments] = useState([]);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [subscriptionEvents, setSubscriptionEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [coachingSessions, setCoachingSessions] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [warningUserId, setWarningUserId] = useState(null);
  const [warningReason, setWarningReason] = useState('');
  
  // Folder management state
  const [folders, setFolders] = useState([]);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    tier_required: 'starter',
    tab_id: 'video-tutorials',
    parent_folder_id: ''
  });
  const [movingItem, setMovingItem] = useState(null); // {type: 'lesson'|'content', id: string}
  const [activeDragItem, setActiveDragItem] = useState(null); // For DragOverlay
  
  // Content tabs state
  const [contentTabs, setContentTabs] = useState([]);
  const [selectedTabFilter, setSelectedTabFilter] = useState('all'); // Filter for viewing content by tab
  const [unassignedContent, setUnassignedContent] = useState({ content: [], lessons: [] });
  
  // Bulk assignment state
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkSelectedItems, setBulkSelectedItems] = useState({ lessons: [], content: [] });
  const [bulkAssignTab, setBulkAssignTab] = useState('');
  const [bulkAssignFolder, setBulkAssignFolder] = useState('');
  
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [feedbackMixId, setFeedbackMixId] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');

  // Media viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState({ url: '', type: 'video', title: '' });
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [lessonForm, setLessonForm] = useState({
    title: '',
    video_url: '',
    tier_required: 'starter',
    duration: '',
    description: '',
    order: 0,
    tab_id: 'video-tutorials',
    folder_id: '',
    notify_members: false
  });

  const [contentForm, setContentForm] = useState({
    title: '',
    type: 'pdf',
    url: '',
    tier_required: 'starter',
    category: 'General',
    description: '',
    tab_id: 'course-documents',
    folder_id: '',
    notify_members: false
  });

  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    tier_required: 'starter'
  });

  // Open media viewer
  const openViewer = (url, type, title) => {
    setViewerData({ url, type, title });
    setViewerOpen(true);
  };

  // Fetch content tabs
  const fetchContentTabs = async () => {
    try {
      const res = await axios.get(`${API}/tabs`);
      setContentTabs(res.data);
    } catch (error) {
      console.error('Failed to fetch tabs:', error);
    }
  };

  // Fetch unassigned content
  const fetchUnassignedContent = async () => {
    try {
      const res = await axios.get(`${API}/admin/content/unassigned`);
      setUnassignedContent(res.data);
    } catch (error) {
      console.error('Failed to fetch unassigned content:', error);
    }
  };

  const fetchData = async (tab) => {
    try {
      // Always fetch tabs and folders for lessons and content tabs
      if (tab === 'lessons' || tab === 'content' || tab === 'folders') {
        const [foldersRes] = await Promise.all([
          axios.get(`${API}/admin/folders`),
        ]);
        setFolders(foldersRes.data);
        await fetchContentTabs();
        await fetchUnassignedContent();
      }
      
      if (tab === 'lessons') {
        const res = await axios.get(`${API}/admin/lessons`);
        setLessons(res.data);
      } else if (tab === 'content') {
        const res = await axios.get(`${API}/admin/content`);
        setContent(res.data);
      } else if (tab === 'users') {
        const res = await axios.get(`${API}/admin/users`);
        setUsers(res.data);
      } else if (tab === 'posts') {
        const res = await axios.get(`${API}/admin/posts`);
        setPosts(res.data);
      } else if (tab === 'community') {
        const [postsRes, statsRes, usersRes, flaggedRes, flagCountRes] = await Promise.all([
          axios.get(`${API}/admin/community/posts`),
          axios.get(`${API}/admin/community/stats`),
          axios.get(`${API}/admin/community/users`),
          axios.get(`${API}/admin/community/flagged`),
          axios.get(`${API}/admin/community/flagged/count`)
        ]);
        setCommunityPosts(postsRes.data);
        setCommunityStats(statsRes.data);
        setCommunityUsers(usersRes.data);
        setFlaggedPosts(flaggedRes.data);
        setFlaggedCount(flagCountRes.data.pending_flags || 0);
      } else if (tab === 'payments') {
        const [paymentsRes, analyticsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/admin/payments/transactions`),
          axios.get(`${API}/admin/payments/analytics`),
          axios.get(`${API}/admin/payments/subscriptions`)
        ]);
        setPayments(paymentsRes.data);
        setPaymentAnalytics(analyticsRes.data);
        setSubscriptionEvents(eventsRes.data);
      } else if (tab === 'announcements') {
        const res = await axios.get(`${API}/admin/announcements`);
        setAnnouncements(res.data);
      } else if (tab === 'coaching') {
        const [sessionsRes, mixesRes] = await Promise.all([
          axios.get(`${API}/admin/coaching/sessions`),
          axios.get(`${API}/admin/coaching/mixes`)
        ]);
        setCoachingSessions(sessionsRes.data);
        setMixes(mixesRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  // Fetch folders separately to ensure they're always available
  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/admin/folders`);
      setFolders(res.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
    // Also fetch folders on initial mount to ensure they're available
    fetchFolders();
    fetchContentTabs();
  }, [activeTab]);

  // Coaching management functions
  const handleUpdateSessionStatus = async (sessionId, status) => {
    try {
      await axios.put(`${API}/admin/coaching/sessions/${sessionId}`, { status });
      toast.success('Session status updated');
      fetchData('coaching');
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    try {
      await axios.delete(`${API}/admin/coaching/sessions/${sessionId}`);
      toast.success('Session deleted');
      fetchData('coaching');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleSubmitFeedback = async (mixId) => {
    if (!feedbackText.trim()) {
      toast.error('Please enter feedback');
      return;
    }
    try {
      await axios.put(`${API}/admin/coaching/mixes/${mixId}`, { 
        feedback: feedbackText,
        status: 'reviewed'
      });
      toast.success('Feedback submitted');
      setFeedbackMixId(null);
      setFeedbackText('');
      fetchData('coaching');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const handleDeleteMix = async (mixId) => {
    if (!window.confirm('Are you sure you want to delete this mix?')) return;
    try {
      await axios.delete(`${API}/admin/coaching/mixes/${mixId}`);
      toast.success('Mix deleted');
      fetchData('coaching');
    } catch (error) {
      toast.error('Failed to delete mix');
    }
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    try {
      if (editingLesson) {
        await axios.put(`${API}/admin/lessons/${editingLesson.id}`, lessonForm);
        toast.success('Lesson updated successfully');
      } else {
        await axios.post(`${API}/admin/lessons`, lessonForm);
        toast.success(lessonForm.notify_members ? 'Lesson created & members notified!' : 'Lesson created successfully');
      }
      setShowLessonForm(false);
      setEditingLesson(null);
      setLessonForm({ title: '', video_url: '', tier_required: 'starter', duration: '', description: '', order: 0, tab_id: 'video-tutorials', folder_id: '', notify_members: false });
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to save lesson');
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await axios.delete(`${API}/admin/lessons/${id}`);
      toast.success('Lesson deleted');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      if (editingContent) {
        await axios.put(`${API}/admin/content/${editingContent.id}`, contentForm);
        toast.success('Content updated successfully');
      } else {
        await axios.post(`${API}/admin/content`, contentForm);
        toast.success(contentForm.notify_members ? 'Content created & members notified!' : 'Content created successfully');
      }
      setShowContentForm(false);
      setEditingContent(null);
      setContentForm({ title: '', type: 'pdf', url: '', tier_required: 'starter', category: 'General', description: '', tab_id: 'course-documents', folder_id: '', notify_members: false });
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await axios.delete(`${API}/admin/content/${id}`);
      toast.success('Content deleted');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/announcements`, announcementForm);
      toast.success('Announcement created successfully');
      setShowAnnouncementForm(false);
      setAnnouncementForm({ title: '', content: '', tier_required: 'starter' });
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // ============ DRAG AND DROP HANDLERS ============
  
  // Handle drag start to track what's being dragged
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragItem(active);
  };
  
  // Handle drag end for all drag operations
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    
    if (!over) return;
    
    const activeData = active.data.current;
    const overId = over.id;
    
    // Check if dropping onto a folder
    if (typeof overId === 'string' && overId.startsWith('folder-')) {
      const targetFolderId = overId.replace('folder-', '');
      const itemType = activeData?.type;
      const itemId = active.id;
      
      if (itemType && itemId) {
        // Move item to the target folder
        const endpoint = itemType === 'lesson' ? 'lessons' : 'content';
        try {
          await axios.put(`${API}/admin/${endpoint}/${itemId}/move`, { 
            folder_id: targetFolderId === 'root' ? null : targetFolderId 
          });
          toast.success(`${itemType === 'lesson' ? 'Lesson' : 'Content'} moved to folder`);
          fetchData(activeTab);
        } catch (error) {
          toast.error('Failed to move item');
        }
        return;
      }
    }
  };
  
  const handleLessonDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    
    if (!over) return;
    
    // Check if dropping onto a folder
    if (typeof over.id === 'string' && over.id.startsWith('folder-')) {
      const targetFolderId = over.id.replace('folder-', '');
      try {
        await axios.put(`${API}/admin/lessons/${active.id}/move`, { 
          folder_id: targetFolderId === 'root' ? null : targetFolderId 
        });
        toast.success('Lesson moved to folder');
        fetchData('lessons');
      } catch (error) {
        toast.error('Failed to move lesson');
      }
      return;
    }
    
    // Otherwise, handle reordering
    if (active.id !== over?.id) {
      const oldIndex = lessons.findIndex(l => l.id === active.id);
      const newIndex = lessons.findIndex(l => l.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      
      const newLessons = arrayMove(lessons, oldIndex, newIndex);
      setLessons(newLessons);
      
      try {
        await axios.put(`${API}/admin/lessons/reorder`, {
          lesson_ids: newLessons.map(l => l.id)
        });
        toast.success('Lessons reordered');
      } catch (error) {
        toast.error('Failed to reorder lessons');
        fetchData('lessons');
      }
    }
  };

  const handleContentDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    
    if (!over) return;
    
    // Check if dropping onto a folder
    if (typeof over.id === 'string' && over.id.startsWith('folder-')) {
      const targetFolderId = over.id.replace('folder-', '');
      try {
        await axios.put(`${API}/admin/content/${active.id}/move`, { 
          folder_id: targetFolderId === 'root' ? null : targetFolderId 
        });
        toast.success('Content moved to folder');
        fetchData('content');
      } catch (error) {
        toast.error('Failed to move content');
      }
      return;
    }
    
    // Otherwise, handle reordering
    if (active.id !== over?.id) {
      const oldIndex = content.findIndex(c => c.id === active.id);
      const newIndex = content.findIndex(c => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      
      const newContent = arrayMove(content, oldIndex, newIndex);
      setContent(newContent);
      
      try {
        await axios.put(`${API}/admin/content/reorder`, {
          content_ids: newContent.map(c => c.id)
        });
        toast.success('Content reordered');
      } catch (error) {
        toast.error('Failed to reorder content');
        fetchData('content');
      }
    }
  };

  const handleFolderDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = folders.findIndex(f => f.id === active.id);
      const newIndex = folders.findIndex(f => f.id === over.id);
      const newFolders = arrayMove(folders, oldIndex, newIndex);
      setFolders(newFolders);
      
      try {
        await axios.put(`${API}/admin/folders/reorder`, {
          folder_ids: newFolders.map(f => f.id)
        });
        toast.success('Folders reordered');
      } catch (error) {
        toast.error('Failed to reorder folders');
        fetchData('lessons');
      }
    }
  };

  // ============ FOLDER MANAGEMENT HANDLERS ============

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data - convert empty string to null for parent_folder_id
      const submitData = {
        ...folderForm,
        parent_folder_id: folderForm.parent_folder_id || null
      };
      
      if (editingFolder) {
        await axios.put(`${API}/admin/folders/${editingFolder.id}`, submitData);
        toast.success('Folder updated successfully');
      } else {
        await axios.post(`${API}/admin/folders`, submitData);
        toast.success('Folder created successfully');
      }
      setShowFolderForm(false);
      setEditingFolder(null);
      setFolderForm({ name: '', description: '', tier_required: 'starter', tab_id: 'video-tutorials', parent_folder_id: '' });
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to save folder');
    }
  };

  const handleDeleteFolder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this folder? Content inside will be moved to root.')) return;
    try {
      await axios.delete(`${API}/admin/folders/${id}`);
      toast.success('Folder deleted');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveToFolder = async (itemId, itemType, folderId) => {
    try {
      await axios.put(`${API}/admin/${itemType}/${itemId}/move`, { folder_id: folderId });
      toast.success(`${itemType === 'lessons' ? 'Lesson' : 'Content'} moved successfully`);
      setMovingItem(null);
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to move item');
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`);
      toast.success('Post deleted');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteCommunityPost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/community/posts/${id}`);
      toast.success('Post deleted');
      fetchData('community');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const res = await axios.put(`${API}/admin/community/posts/${postId}/pin`);
      toast.success(res.data.pinned ? 'Post pinned' : 'Post unpinned');
      fetchData('community');
    } catch (error) {
      toast.error('Failed to pin/unpin post');
    }
  };

  // Handle flagged content review
  const handleReviewFlag = async (flagId, action) => {
    try {
      await axios.put(`${API}/admin/community/flagged/${flagId}/review?action=${action}`);
      toast.success(action === 'dismiss' ? 'Flag dismissed' : 'Post removed');
      fetchData('community');
    } catch (error) {
      toast.error('Failed to review flag');
    }
  };

  // Handle user warning
  const handleWarnUser = async (userId) => {
    if (!warningReason.trim()) {
      toast.error('Please provide a reason for the warning');
      return;
    }
    try {
      await axios.post(`${API}/admin/community/users/${userId}/warn?reason=${encodeURIComponent(warningReason)}`);
      toast.success('Warning issued to user');
      setWarningUserId(null);
      setWarningReason('');
      fetchData('community');
    } catch (error) {
      toast.error('Failed to warn user');
    }
  };

  // Handle user ban toggle
  const handleToggleBan = async (userId) => {
    if (!window.confirm('Are you sure you want to toggle this user\'s community ban status?')) return;
    try {
      const res = await axios.put(`${API}/admin/community/users/${userId}/ban`);
      toast.success(res.data.banned ? 'User banned from community' : 'User unbanned');
      fetchData('community');
    } catch (error) {
      toast.error('Failed to toggle ban status');
    }
  };

  const getFilteredCommunityPosts = () => {
    if (communityFilter === 'all') return communityPosts;
    if (communityFilter === 'pinned') return communityPosts.filter(p => p.pinned);
    if (communityFilter === 'flagged') return communityPosts.filter(p => (p.flag_count || 0) > 0);
    return communityPosts.filter(p => p.room_id === communityFilter);
  };

  const handleUpdateMembership = async (userId, tier, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/membership`, null, {
        params: { tier, status }
      });
      toast.success('Membership updated');
      fetchData(activeTab);
    } catch (error) {
      toast.error('Failed to update membership');
    }
  };

  return (
    <div className="min-h-screen py-12" style={{ forcedColorAdjust: 'none' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold" style={{ color: '#ffffff' }}>Admin Dashboard</h1>
          <a href="/admin/referrals">
            <Button variant="outline" className="flex items-center gap-2" style={{ borderColor: '#D4AF37', color: '#D4AF37' }}>
              <Users size={16} />
              Referral Tracking
            </Button>
          </a>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-8 grid grid-cols-8 gap-2">
            <TabsTrigger value="lessons"><FileText size={16} className="mr-2" />Lessons</TabsTrigger>
            <TabsTrigger value="content"><Music size={16} className="mr-2" />Content</TabsTrigger>
            <TabsTrigger value="coaching"><Headphones size={16} className="mr-2" />Coaching</TabsTrigger>
            <TabsTrigger value="community"><MessageSquare size={16} className="mr-2" />Community</TabsTrigger>
            <TabsTrigger value="announcements"><Bell size={16} className="mr-2" />Announcements</TabsTrigger>
            <TabsTrigger value="users"><Users size={16} className="mr-2" />Users</TabsTrigger>
            <TabsTrigger value="posts"><MessageSquare size={16} className="mr-2" />Posts</TabsTrigger>
            <TabsTrigger value="payments"><CreditCard size={16} className="mr-2" />Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="lessons">
            <div className="mb-4 flex gap-2 flex-wrap">
              <Button onClick={async () => { 
                setShowLessonForm(true); 
                setEditingLesson(null);
                // Ensure folders are loaded when form opens
                if (folders.length === 0) {
                  try {
                    const foldersRes = await axios.get(`${API}/admin/folders`);
                    setFolders(foldersRes.data);
                  } catch (e) {
                    console.error('Failed to fetch folders:', e);
                  }
                }
              }} className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>
                <Plus size={16} className="mr-2" /> Add Lesson
              </Button>
              <Button onClick={() => { setShowFolderForm(true); setEditingFolder(null); }} variant="outline" className="rounded-full" style={{ borderColor: '#D4AF37', color: '#D4AF37' }}>
                <FolderPlus size={16} className="mr-2" /> Create Folder
              </Button>
              <Button onClick={() => setShowBulkAssign(!showBulkAssign)} variant="outline" className="rounded-full" style={{ borderColor: showBulkAssign ? '#22c55e' : '#D4AF37', color: showBulkAssign ? '#22c55e' : '#D4AF37' }}>
                <RefreshCw size={16} className="mr-2" /> Bulk Assign ({unassignedContent.lessons.length + unassignedContent.content.length} unassigned)
              </Button>
            </div>

            {/* Bulk Assignment Panel */}
            {showBulkAssign && (
              <div className="glass-panel rounded-2xl p-6 mb-6 border" style={{ borderColor: '#22c55e', background: 'rgba(34, 197, 94, 0.05)' }}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: '#22c55e' }}>
                  <RefreshCw size={20} />
                  Bulk Content Assignment
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label>Target Tab</Label>
                    <select 
                      value={bulkAssignTab} 
                      onChange={(e) => { setBulkAssignTab(e.target.value); setBulkAssignFolder(''); }} 
                      className="w-full p-2 rounded-md border border-border bg-background"
                    >
                      <option value="">Select tab...</option>
                      {contentTabs.map((tab) => (
                        <option key={tab.id} value={tab.id}>{tab.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Target Folder</Label>
                    <select 
                      value={bulkAssignFolder} 
                      onChange={(e) => setBulkAssignFolder(e.target.value)} 
                      className="w-full p-2 rounded-md border border-border bg-background"
                      disabled={!bulkAssignTab}
                    >
                      <option value="">Select folder...</option>
                      {folders.filter(f => f.tab_id === bulkAssignTab).map((folder) => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={async () => {
                        if (!bulkAssignTab || !bulkAssignFolder) {
                          toast.error('Select both tab and folder');
                          return;
                        }
                        if (bulkSelectedItems.lessons.length === 0 && bulkSelectedItems.content.length === 0) {
                          toast.error('Select items to assign');
                          return;
                        }
                        try {
                          await axios.put(`${API}/admin/content/bulk-assign`, {
                            lesson_ids: bulkSelectedItems.lessons,
                            content_ids: bulkSelectedItems.content,
                            tab_id: bulkAssignTab,
                            folder_id: bulkAssignFolder
                          });
                          toast.success(`Assigned ${bulkSelectedItems.lessons.length + bulkSelectedItems.content.length} items`);
                          setBulkSelectedItems({ lessons: [], content: [] });
                          fetchUnassignedContent();
                          fetchData('lessons');
                        } catch (error) {
                          toast.error('Failed to assign content');
                        }
                      }}
                      className="w-full rounded-full"
                      style={{ background: '#22c55e', color: '#000', fontWeight: '600' }}
                      disabled={!bulkAssignTab || !bulkAssignFolder}
                    >
                      Assign Selected ({bulkSelectedItems.lessons.length + bulkSelectedItems.content.length})
                    </Button>
                  </div>
                </div>

                {/* Unassigned Items */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Unassigned Lessons */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm">Unassigned Lessons ({unassignedContent.lessons.length})</Label>
                      <button 
                        onClick={() => setBulkSelectedItems(prev => ({
                          ...prev,
                          lessons: prev.lessons.length === unassignedContent.lessons.length ? [] : unassignedContent.lessons.map(l => l.id)
                        }))}
                        className="text-xs px-2 py-1 rounded" 
                        style={{ color: '#D4AF37' }}
                      >
                        {bulkSelectedItems.lessons.length === unassignedContent.lessons.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-border p-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      {unassignedContent.lessons.length === 0 ? (
                        <p className="text-sm text-center py-4" style={{ color: '#666' }}>All lessons assigned</p>
                      ) : (
                        unassignedContent.lessons.map((lesson) => (
                          <label key={lesson.id} className="flex items-center gap-2 p-2 rounded hover:bg-white/5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkSelectedItems.lessons.includes(lesson.id)}
                              onChange={(e) => {
                                setBulkSelectedItems(prev => ({
                                  ...prev,
                                  lessons: e.target.checked 
                                    ? [...prev.lessons, lesson.id]
                                    : prev.lessons.filter(id => id !== lesson.id)
                                }));
                              }}
                              className="rounded"
                            />
                            <Video size={14} style={{ color: '#D4AF37' }} />
                            <span className="text-sm truncate" style={{ color: '#fff' }}>{lesson.title}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Unassigned Content */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm">Unassigned Content ({unassignedContent.content.length})</Label>
                      <button 
                        onClick={() => setBulkSelectedItems(prev => ({
                          ...prev,
                          content: prev.content.length === unassignedContent.content.length ? [] : unassignedContent.content.map(c => c.id)
                        }))}
                        className="text-xs px-2 py-1 rounded" 
                        style={{ color: '#D4AF37' }}
                      >
                        {bulkSelectedItems.content.length === unassignedContent.content.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-border p-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      {unassignedContent.content.length === 0 ? (
                        <p className="text-sm text-center py-4" style={{ color: '#666' }}>All content assigned</p>
                      ) : (
                        unassignedContent.content.map((item) => (
                          <label key={item.id} className="flex items-center gap-2 p-2 rounded hover:bg-white/5 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={bulkSelectedItems.content.includes(item.id)}
                              onChange={(e) => {
                                setBulkSelectedItems(prev => ({
                                  ...prev,
                                  content: e.target.checked 
                                    ? [...prev.content, item.id]
                                    : prev.content.filter(id => id !== item.id)
                                }));
                              }}
                              className="rounded"
                            />
                            {item.type === 'pdf' && <FileText size={14} style={{ color: '#D4AF37' }} />}
                            {item.type === 'audio' && <Music size={14} style={{ color: '#D4AF37' }} />}
                            {item.type === 'quiz' && <AlertCircle size={14} style={{ color: '#D4AF37' }} />}
                            {item.type === 'video' && <Video size={14} style={{ color: '#D4AF37' }} />}
                            <span className="text-sm truncate" style={{ color: '#fff' }}>{item.title}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}>{item.type}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Folder Form */}
            {showFolderForm && (
              <div className="glass-panel rounded-2xl p-6 mb-6 border" style={{ borderColor: '#D4AF37' }}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Folder size={20} style={{ color: '#D4AF37' }} />
                  {editingFolder ? 'Edit Folder' : 'Create New Folder'}
                </h3>
                <form onSubmit={handleCreateFolder} className="space-y-4">
                  <div>
                    <Label>Folder Name</Label>
                    <Input value={folderForm.name} onChange={(e) => setFolderForm({...folderForm, name: e.target.value})} required placeholder="e.g., Beginner Lessons" />
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Input value={folderForm.description} onChange={(e) => setFolderForm({...folderForm, description: e.target.value})} placeholder="Brief description of folder contents" />
                  </div>
                  <div>
                    <Label>Content Tab <span className="text-red-500">*</span></Label>
                    <select 
                      value={folderForm.tab_id} 
                      onChange={(e) => setFolderForm({...folderForm, tab_id: e.target.value, parent_folder_id: ''})} 
                      className="w-full p-2 rounded-md border border-border bg-background"
                      required
                    >
                      {contentTabs.map((tab) => (
                        <option key={tab.id} value={tab.id}>{tab.name}</option>
                      ))}
                    </select>
                    <p className="text-xs mt-1" style={{ color: '#a3a3a3' }}>Select which tab this folder will appear under</p>
                  </div>
                  <div>
                    <Label>Parent Folder (optional - for nested folders)</Label>
                    <select 
                      value={folderForm.parent_folder_id} 
                      onChange={(e) => setFolderForm({...folderForm, parent_folder_id: e.target.value})} 
                      className="w-full p-2 rounded-md border border-border bg-background"
                    >
                      <option value="">None (Root Level)</option>
                      {folders
                        .filter(f => f.tab_id === folderForm.tab_id && f.id !== editingFolder?.id)
                        .map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.parent_folder_id ? '↳ ' : ''}{folder.name}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs mt-1" style={{ color: '#a3a3a3' }}>Leave empty to create a root-level folder, or select a parent to create a subfolder</p>
                  </div>
                  <div>
                    <Label>Tier Access Required</Label>
                    <select value={folderForm.tier_required} onChange={(e) => setFolderForm({...folderForm, tier_required: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="free">Free</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>Save Folder</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowFolderForm(false); setEditingFolder(null); }} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Move to Folder Modal */}
            {movingItem && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="glass-panel rounded-2xl p-6 border max-w-md w-full mx-4" style={{ background: '#1a1a1a' }}>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Move size={20} style={{ color: '#D4AF37' }} />
                    Move to Folder
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleMoveToFolder(movingItem.id, movingItem.type, null)}
                      className="w-full p-3 rounded-lg border border-border hover:border-yellow-500/50 text-left flex items-center gap-2"
                    >
                      <FolderOpen size={18} style={{ color: '#a3a3a3' }} />
                      <span style={{ color: '#ffffff' }}>Root (No Folder)</span>
                    </button>
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        onClick={() => handleMoveToFolder(movingItem.id, movingItem.type, folder.id)}
                        className="w-full p-3 rounded-lg border border-border hover:border-yellow-500/50 text-left flex items-center gap-2"
                      >
                        <Folder size={18} style={{ color: '#D4AF37' }} />
                        <span style={{ color: '#ffffff' }}>{folder.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {folder.tier_required}
                        </span>
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" onClick={() => setMovingItem(null)} className="w-full mt-4 rounded-full">Cancel</Button>
                </div>
              </div>
            )}

            {showLessonForm && (
              <div className="glass-panel rounded-2xl p-6 mb-6 border">
                <h3 className="text-xl font-bold mb-4">{editingLesson ? 'Edit Lesson' : 'Create New Lesson'}</h3>
                <form onSubmit={handleCreateLesson} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={lessonForm.title} onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Video URL (Google Drive or YouTube)</Label>
                    <Input 
                      value={lessonForm.video_url} 
                      onChange={(e) => setLessonForm({...lessonForm, video_url: e.target.value})} 
                      required 
                      placeholder="https://drive.google.com/file/d/FILE_ID/view or YouTube URL"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      For Google Drive: Share file → &quot;Anyone with the link&quot; → Copy link
                    </p>
                  </div>
                  {lessonForm.video_url && (
                    <div>
                      <Label className="mb-2 block">URL Preview</Label>
                      <GoogleDrivePreview url={lessonForm.video_url} type="video" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Content Tab <span className="text-red-500">*</span></Label>
                      <select 
                        value={lessonForm.tab_id} 
                        onChange={(e) => {
                          setLessonForm({...lessonForm, tab_id: e.target.value, folder_id: ''});
                        }} 
                        className="w-full p-2 rounded-md border border-border bg-background"
                        required
                      >
                        {contentTabs.map((tab) => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Folder <span className="text-red-500">*</span></Label>
                      <select 
                        value={lessonForm.folder_id} 
                        onChange={(e) => setLessonForm({...lessonForm, folder_id: e.target.value})} 
                        className="w-full p-2 rounded-md border border-border bg-background"
                        required
                      >
                        <option value="">Select a folder...</option>
                        {folders.filter(f => f.tab_id === lessonForm.tab_id).map((folder) => (
                          <option key={folder.id} value={folder.id}>{folder.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Tier Required</Label>
                    <select value={lessonForm.tier_required} onChange={(e) => setLessonForm({...lessonForm, tier_required: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="free">Free</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div>
                    <Label>Duration (optional)</Label>
                    <Input value={lessonForm.duration} onChange={(e) => setLessonForm({...lessonForm, duration: e.target.value})} placeholder="e.g., 15:30" />
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Textarea value={lessonForm.description} onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})} />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input type="number" value={lessonForm.order} onChange={(e) => setLessonForm({...lessonForm, order: parseInt(e.target.value)})} />
                  </div>
                  {/* Notify Members Checkbox - only show for new lessons */}
                  {!editingLesson && (
                    <div className="flex items-center gap-3 p-4 rounded-lg border" style={{ background: 'rgba(212, 175, 55, 0.05)', borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                      <input
                        type="checkbox"
                        id="notify-members-lesson"
                        checked={lessonForm.notify_members}
                        onChange={(e) => setLessonForm({...lessonForm, notify_members: e.target.checked})}
                        className="w-5 h-5 rounded border-2 accent-yellow-500"
                      />
                      <div>
                        <Label htmlFor="notify-members-lesson" className="cursor-pointer font-semibold" style={{ color: '#D4AF37' }}>
                          Notify Members
                        </Label>
                        <p className="text-xs" style={{ color: '#a3a3a3' }}>
                          Send email to all members with access to this content tier
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>Save</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowLessonForm(false); setEditingLesson(null); }} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Folders Section - Drop targets for drag and drop */}
            {folders.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
                  <FolderOpen size={18} style={{ color: '#D4AF37' }} /> Folders
                  <span className="text-xs text-muted-foreground">(Drop items here to move)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {folders.map((folder) => {
                    const parentFolder = folders.find(f => f.id === folder.parent_folder_id);
                    const subfolderCount = folders.filter(f => f.parent_folder_id === folder.id).length;
                    return (
                      <DroppableFolder 
                        key={folder.id} 
                        id={folder.id}
                        name={folder.name}
                        tierRequired={folder.tier_required}
                        tabId={folder.tab_id}
                        isActive={activeDragItem !== null}
                        parentFolderName={parentFolder?.name}
                        subfolderCount={subfolderCount}
                      >
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setEditingFolder(folder); setFolderForm({...folder, parent_folder_id: folder.parent_folder_id || ''}); setShowFolderForm(true); }}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteFolder(folder.id)} style={{ color: '#ef4444' }}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </DroppableFolder>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lessons List with Drag and Drop */}
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
              <Video size={18} style={{ color: '#D4AF37' }} /> Lessons
              <span className="text-xs text-muted-foreground">(Drag to reorder or drop on folder)</span>
            </h3>
            <DndContext 
              sensors={sensors} 
              collisionDetection={rectIntersection} 
              onDragStart={handleDragStart}
              onDragEnd={handleLessonDragEnd}
            >
              <SortableContext items={lessons.map(l => l.id)} strategy={verticalListSortingStrategy}>
                <div className="grid grid-cols-1 gap-4">
                  {lessons.map((lesson) => (
                    <SortableItem key={lesson.id} id={lesson.id}>
                      <div className="glass-panel rounded-xl p-4 border flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Video size={16} style={{ color: '#D4AF37' }} />
                            <h4 className="font-bold" style={{ color: '#ffffff' }}>{lesson.title}</h4>
                            {lesson.folder_id && (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                                📁 {folders.find(f => f.id === lesson.folder_id)?.name || 'Unknown'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm" style={{ color: '#a3a3a3' }}>
                            Tier: {lesson.tier_required} | Order: {lesson.order}
                            {lesson.duration && ` | Duration: ${lesson.duration}`}
                          </p>
                          {lesson.video_url && (
                            <p className="text-xs mt-1 truncate max-w-md" style={{ color: '#666' }}>
                              {isGoogleDriveUrl(lesson.video_url) ? `Google Drive: ${extractGoogleDriveFileId(lesson.video_url)}` : lesson.video_url}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setMovingItem({ id: lesson.id, type: 'lessons' })} title="Move to folder">
                            <Move size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openViewer(lesson.video_url, 'video', lesson.title)} title="Preview">
                            <Eye size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingLesson(lesson); setLessonForm(lesson); setShowLessonForm(true); }}>
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteLesson(lesson.id)} style={{ color: '#ef4444' }}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </SortableItem>
                  ))}
                  {lessons.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No lessons yet. Click &quot;Add Lesson&quot; to create your first lesson.
                    </div>
                  )}
                </div>
              </SortableContext>
              
              {/* Drag Overlay for visual feedback */}
              <DragOverlay>
                {activeDragItem ? (
                  <div className="glass-panel rounded-xl p-4 border opacity-80" style={{ background: '#1a1a2e', borderColor: '#D4AF37' }}>
                    <div className="flex items-center gap-2">
                      <GripVertical size={18} style={{ color: '#D4AF37' }} />
                      <Video size={16} style={{ color: '#D4AF37' }} />
                      <span style={{ color: '#fff' }}>
                        {lessons.find(l => l.id === activeDragItem.id)?.title || 'Moving item...'}
                      </span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </TabsContent>

          <TabsContent value="content">
            <div className="mb-4">
              <Button onClick={async () => { 
                setShowContentForm(true); 
                setEditingContent(null);
                // Ensure folders are loaded when form opens
                if (folders.length === 0) {
                  await fetchFolders();
                }
              }} className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>
                <Plus size={16} className="mr-2" /> Add Content
              </Button>
            </div>

            {showContentForm && (
              <div className="glass-panel rounded-2xl p-6 mb-6 border">
                <h3 className="text-xl font-bold mb-4">{editingContent ? 'Edit Content' : 'Create New Content'}</h3>
                <form onSubmit={handleCreateContent} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={contentForm.title} onChange={(e) => setContentForm({...contentForm, title: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <select value={contentForm.type} onChange={(e) => setContentForm({...contentForm, type: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="pdf">PDF Document</option>
                      <option value="audio">Audio File</option>
                      <option value="video">Video</option>
                      <option value="quiz">Quiz</option>
                    </select>
                  </div>
                  <div>
                    <Label>File URL (Google Drive)</Label>
                    <Input 
                      value={contentForm.url} 
                      onChange={(e) => setContentForm({...contentForm, url: e.target.value})} 
                      required 
                      placeholder="https://drive.google.com/file/d/FILE_ID/view"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Upload your file to Google Drive → Share → &quot;Anyone with the link&quot; → Copy link
                    </p>
                  </div>
                  {contentForm.url && (
                    <div>
                      <Label className="mb-2 block">URL Preview</Label>
                      <GoogleDrivePreview url={contentForm.url} type={contentForm.type} />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Content Tab <span className="text-red-500">*</span></Label>
                      <select 
                        value={contentForm.tab_id} 
                        onChange={(e) => {
                          setContentForm({...contentForm, tab_id: e.target.value, folder_id: ''});
                        }} 
                        className="w-full p-2 rounded-md border border-border bg-background"
                        required
                      >
                        {contentTabs.map((tab) => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Folder <span className="text-red-500">*</span></Label>
                      <select 
                        value={contentForm.folder_id} 
                        onChange={(e) => setContentForm({...contentForm, folder_id: e.target.value})} 
                        className="w-full p-2 rounded-md border border-border bg-background"
                        required
                      >
                        <option value="">Select a folder...</option>
                        {folders.filter(f => f.tab_id === contentForm.tab_id).map((folder) => (
                          <option key={folder.id} value={folder.id}>{folder.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Tier Required</Label>
                    <select value={contentForm.tier_required} onChange={(e) => setContentForm({...contentForm, tier_required: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="free">Free</option>
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select value={contentForm.category} onChange={(e) => setContentForm({...contentForm, category: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="General">General</option>
                      <option value="DJ Tools">DJ Tools</option>
                      <option value="SFX Packs">SFX Packs</option>
                      <option value="Templates">Templates</option>
                      <option value="eBooks">eBooks</option>
                      <option value="Music Resources">Music Resources</option>
                    </select>
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Textarea value={contentForm.description} onChange={(e) => setContentForm({...contentForm, description: e.target.value})} />
                  </div>
                  {/* Notify Members Checkbox - only show for new content */}
                  {!editingContent && (
                    <div className="flex items-center gap-3 p-4 rounded-lg border" style={{ background: 'rgba(212, 175, 55, 0.05)', borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                      <input
                        type="checkbox"
                        id="notify-members-content"
                        checked={contentForm.notify_members}
                        onChange={(e) => setContentForm({...contentForm, notify_members: e.target.checked})}
                        className="w-5 h-5 rounded border-2 accent-yellow-500"
                      />
                      <div>
                        <Label htmlFor="notify-members-content" className="cursor-pointer font-semibold" style={{ color: '#D4AF37' }}>
                          Notify Members
                        </Label>
                        <p className="text-xs" style={{ color: '#a3a3a3' }}>
                          Send email to all members with access to this content tier
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>Save</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowContentForm(false); setEditingContent(null); }} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Content List with Drag and Drop */}
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
              <FileText size={18} style={{ color: '#D4AF37' }} /> Resources
              <span className="text-xs text-muted-foreground">(Drag to reorder or drop on folder)</span>
            </h3>
            
            {/* Folders as drop targets in Content tab */}
            {folders.length > 0 && (
              <div className="mb-6">
                <p className="text-sm mb-2" style={{ color: '#a3a3a3' }}>
                  Drop content items on folders below to organize:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {folders.map((folder) => {
                    const parentFolder = folders.find(f => f.id === folder.parent_folder_id);
                    const subfolderCount = folders.filter(f => f.parent_folder_id === folder.id).length;
                    return (
                      <DroppableFolder 
                        key={folder.id} 
                        id={folder.id}
                        name={folder.name}
                        tierRequired={folder.tier_required}
                        tabId={folder.tab_id}
                        isActive={activeDragItem !== null}
                        parentFolderName={parentFolder?.name}
                        subfolderCount={subfolderCount}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            
            <DndContext 
              sensors={sensors} 
              collisionDetection={rectIntersection}
              onDragStart={handleDragStart} 
              onDragEnd={handleContentDragEnd}
            >
              <SortableContext items={content.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <div className="grid grid-cols-1 gap-4">
                  {content.map((item) => (
                    <SortableItem key={item.id} id={item.id}>
                      <div className="glass-panel rounded-xl p-4 border flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.type === 'pdf' && <FileText size={16} style={{ color: '#D4AF37' }} />}
                            {item.type === 'audio' && <Music size={16} style={{ color: '#D4AF37' }} />}
                            {item.type === 'video' && <Video size={16} style={{ color: '#D4AF37' }} />}
                            <h4 className="font-bold" style={{ color: '#ffffff' }}>{item.title}</h4>
                            {item.folder_id && (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                                📁 {folders.find(f => f.id === item.folder_id)?.name || 'Unknown'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm" style={{ color: '#a3a3a3' }}>
                            Type: {item.type} | Tier: {item.tier_required} | Category: {item.category}
                          </p>
                          {item.url && (
                            <p className="text-xs mt-1 truncate max-w-md" style={{ color: '#666' }}>
                              {isGoogleDriveUrl(item.url) ? `Google Drive: ${extractGoogleDriveFileId(item.url)}` : item.url}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setMovingItem({ id: item.id, type: 'content' })} title="Move to folder">
                            <Move size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openViewer(item.url, item.type, item.title)} title="Preview">
                            <Eye size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingContent(item); setContentForm(item); setShowContentForm(true); }}>
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteContent(item.id)} style={{ color: '#ef4444' }}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </SortableItem>
                  ))}
                  {content.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No content yet. Click &quot;Add Content&quot; to upload your first resource.
                    </div>
                  )}
                </div>
              </SortableContext>
              
              {/* Drag Overlay for content */}
              <DragOverlay>
                {activeDragItem ? (
                  <div className="glass-panel rounded-xl p-4 border opacity-80" style={{ background: '#1a1a2e', borderColor: '#D4AF37' }}>
                    <div className="flex items-center gap-2">
                      <GripVertical size={18} style={{ color: '#D4AF37' }} />
                      <FileText size={16} style={{ color: '#D4AF37' }} />
                      <span style={{ color: '#fff' }}>
                        {content.find(c => c.id === activeDragItem.id)?.title || 'Moving item...'}
                      </span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </TabsContent>

          <TabsContent value="coaching">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Coaching Sessions Section */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Calendar size={20} style={{ color: '#D4AF37' }} />
                  Coaching Sessions
                </h3>
                <div className="space-y-4">
                  {coachingSessions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground glass-panel rounded-xl border">
                      No coaching sessions booked yet.
                    </div>
                  ) : (
                    coachingSessions.map((session) => (
                      <div key={session.id} className="glass-panel rounded-xl p-4 border">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold" style={{ color: '#ffffff' }}>
                              {session.user_name || 'Unknown User'}
                            </p>
                            <p className="text-sm" style={{ color: '#a3a3a3' }}>{session.user_email}</p>
                          </div>
                          <select
                            value={session.status}
                            onChange={(e) => handleUpdateSessionStatus(session.id, e.target.value)}
                            className="p-2 rounded-md border border-border bg-background text-sm"
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-4 text-sm" style={{ color: '#a3a3a3' }}>
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {new Date(session.date).toLocaleDateString()}
                          </span>
                          <span>{session.time}</span>
                        </div>
                        <div className="mt-3 flex justify-end">
                          <Button size="sm" variant="outline" onClick={() => handleDeleteSession(session.id)} style={{ color: '#ef4444' }}>
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Mix Reviews Section */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Headphones size={20} style={{ color: '#D4AF37' }} />
                  Mix Reviews
                </h3>
                <div className="space-y-4">
                  {mixes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground glass-panel rounded-xl border">
                      No mixes uploaded for review yet.
                    </div>
                  ) : (
                    mixes.map((mix) => (
                      <div key={mix.id} className="glass-panel rounded-xl p-4 border">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold" style={{ color: '#ffffff' }}>{mix.title}</p>
                            <p className="text-sm" style={{ color: '#a3a3a3' }}>
                              By: {mix.user_name || 'Unknown'} ({mix.user_email})
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            mix.status === 'reviewed' 
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {mix.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                          </span>
                        </div>
                        
                        {mix.notes && (
                          <div className="mb-3 p-2 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <span className="font-semibold">Notes:</span> {mix.notes}
                          </div>
                        )}

                        <div className="flex items-center gap-2 mb-3">
                          <a
                            href={mix.mix_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm flex items-center gap-1 hover:underline"
                            style={{ color: '#D4AF37' }}
                          >
                            <ExternalLink size={14} /> Listen to Mix
                          </a>
                        </div>

                        {feedbackMixId === mix.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={feedbackText}
                              onChange={(e) => setFeedbackText(e.target.value)}
                              placeholder="Write your feedback for this mix..."
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleSubmitFeedback(mix.id)} style={{ background: '#D4AF37', color: '#141414' }}>
                                Submit Feedback
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setFeedbackMixId(null); setFeedbackText(''); }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {mix.feedback && (
                              <div className="mb-3 p-3 rounded-lg border" style={{ background: 'rgba(212, 175, 55, 0.05)', borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                                <span className="text-xs font-semibold" style={{ color: '#D4AF37' }}>Your Feedback:</span>
                                <p className="text-sm mt-1">{mix.feedback}</p>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => { setFeedbackMixId(mix.id); setFeedbackText(mix.feedback || ''); }}
                              >
                                <Edit size={14} className="mr-1" /> {mix.feedback ? 'Edit Feedback' : 'Add Feedback'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteMix(mix.id)} style={{ color: '#ef4444' }}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="community">
            {/* Community Stats Overview */}
            {communityStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="glass-panel rounded-xl p-4 border text-center">
                  <div className="text-3xl font-bold" style={{ color: '#D4AF37' }}>{communityStats.total_posts}</div>
                  <div className="text-sm" style={{ color: '#a3a3a3' }}>Total Posts</div>
                </div>
                <div className="glass-panel rounded-xl p-4 border text-center">
                  <div className="text-3xl font-bold" style={{ color: '#D4AF37' }}>{communityStats.pinned_posts}</div>
                  <div className="text-sm" style={{ color: '#a3a3a3' }}>Pinned Posts</div>
                </div>
                <div className="glass-panel rounded-xl p-4 border text-center">
                  <div className="text-3xl font-bold" style={{ color: '#D4AF37' }}>{communityStats.total_follows}</div>
                  <div className="text-sm" style={{ color: '#a3a3a3' }}>Total Follows</div>
                </div>
                <div className="glass-panel rounded-xl p-4 border text-center">
                  <div className="text-3xl font-bold" style={{ color: '#22c55e' }}>{communityStats.recent_posts_7d}</div>
                  <div className="text-sm" style={{ color: '#a3a3a3' }}>Posts (7 days)</div>
                </div>
                <div className="glass-panel rounded-xl p-4 border text-center" style={{ background: flaggedCount > 0 ? 'rgba(239, 68, 68, 0.1)' : '' }}>
                  <div className="text-3xl font-bold" style={{ color: flaggedCount > 0 ? '#ef4444' : '#D4AF37' }}>{flaggedCount}</div>
                  <div className="text-sm flex items-center justify-center gap-1" style={{ color: '#a3a3a3' }}>
                    <Flag size={12} /> Flagged
                  </div>
                </div>
              </div>
            )}

            {/* Flagged Posts Alert */}
            {flaggedPosts.length > 0 && (
              <div className="glass-panel rounded-xl p-4 border mb-6" style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
                <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#ef4444' }}>
                  <AlertTriangle size={18} />
                  Flagged Content Requiring Review ({flaggedPosts.length})
                </h4>
                <div className="space-y-3">
                  {flaggedPosts.slice(0, 5).map((flag) => (
                    <div key={flag.id} className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm mb-1" style={{ color: '#ffffff' }}>
                            <span className="font-semibold">{flag.post_author_name}</span>
                            <span style={{ color: '#a3a3a3' }}> in {flag.room_id}</span>
                          </p>
                          <p className="text-sm mb-2" style={{ color: '#e5e5e5' }}>
                            {flag.post_content}...
                          </p>
                          <p className="text-xs" style={{ color: '#a3a3a3' }}>
                            Flagged by {flag.flagged_by_name} • Reason: {flag.reason}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleReviewFlag(flag.id, 'dismiss')}
                            title="Dismiss flag"
                            style={{ color: '#22c55e', borderColor: '#22c55e' }}
                          >
                            <CheckCircle size={14} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleReviewFlag(flag.id, 'remove_post')}
                            title="Remove post"
                            style={{ color: '#ef4444', borderColor: '#ef4444' }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {flaggedPosts.length > 5 && (
                    <p className="text-sm text-center" style={{ color: '#a3a3a3' }}>
                      +{flaggedPosts.length - 5} more flagged posts
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Room Stats */}
            {communityStats && (
              <div className="glass-panel rounded-xl p-4 border mb-6">
                <h4 className="font-semibold mb-3" style={{ color: '#ffffff' }}>Posts by Room</h4>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  {Object.entries(communityStats.posts_by_room || {}).map(([room, count]) => (
                    <div key={room} className="text-center p-2 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.05)' }}>
                      <div className="text-lg font-bold" style={{ color: '#D4AF37' }}>{count}</div>
                      <div className="text-xs truncate" style={{ color: '#a3a3a3' }}>{room}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filter & Posts Management */}
            <div className="flex items-center gap-4 mb-6">
              <Label className="whitespace-nowrap">Filter by:</Label>
              <select 
                value={communityFilter} 
                onChange={(e) => setCommunityFilter(e.target.value)}
                className="p-2 rounded-md border border-border bg-background"
              >
                <option value="all">All Posts</option>
                <option value="pinned">📌 Pinned Only</option>
                <option value="flagged">🚩 Flagged Posts</option>
                <option value="general">💬 General DJ Chat</option>
                <option value="beginner-lab">🎧 Beginner Lab</option>
                <option value="mix-feedback">🔥 Mix Feedback</option>
                <option value="dj-growth">📈 DJ Growth</option>
                <option value="track-discovery">🎵 Track Discovery</option>
                <option value="local-connections">🌍 Local Connections</option>
              </select>
              <span className="text-sm" style={{ color: '#a3a3a3' }}>
                Showing {getFilteredCommunityPosts().length} posts
              </span>
            </div>

            {/* Community Posts */}
            <div className="grid grid-cols-1 gap-4 mb-8">
              {getFilteredCommunityPosts().map((post) => (
                <div key={post.id} className={`glass-panel rounded-xl p-4 border ${post.pinned ? 'border-yellow-500/50' : ''} ${(post.flag_count || 0) > 0 ? 'border-red-500/30' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <UserCircle size={32} style={{ color: '#D4AF37' }} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold" style={{ color: '#ffffff' }}>
                            {post.user_dj_name || post.user_name}
                          </span>
                          {post.pinned && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center gap-1">
                              <Pin size={10} /> Pinned
                            </span>
                          )}
                          {(post.flag_count || 0) > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 flex items-center gap-1">
                              <Flag size={10} /> {post.flag_count} flag{post.flag_count > 1 ? 's' : ''}
                            </span>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                            {post.user_membership || 'none'}
                          </span>
                        </div>
                        <div className="text-xs" style={{ color: '#a3a3a3' }}>
                          {post.user_email} • {post.room_id} • {new Date(post.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handlePinPost(post.id)}
                        title={post.pinned ? 'Unpin post' : 'Pin post'}
                        style={post.pinned ? { color: '#eab308', borderColor: '#eab308' } : {}}
                      >
                        <Pin size={14} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeleteCommunityPost(post.id)} 
                        style={{ color: '#ef4444' }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm mb-3" style={{ color: '#e5e5e5' }}>{post.content}</p>
                  {/* Reactions display */}
                  {post.reactions && Object.keys(post.reactions).length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(post.reactions).map(([emoji, users]) => (
                        users.length > 0 && (
                          <span key={emoji} className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            {emoji} {users.length}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {getFilteredCommunityPosts().length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No posts found for this filter.
                </div>
              )}
            </div>

            {/* Community Users Section */}
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Users size={20} style={{ color: '#D4AF37' }} />
              Active Community Members
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Member</th>
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Tier</th>
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Posts</th>
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Followers</th>
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Status</th>
                    <th className="text-left p-3" style={{ color: '#ffffff' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {communityUsers.slice(0, 20).map((cUser) => (
                    <tr key={cUser.id} className={`border-b border-border/50 ${cUser.community_banned ? 'bg-red-500/5' : ''}`}>
                      <td className="p-3">
                        <div>
                          <span style={{ color: '#ffffff' }}>{cUser.dj_name || cUser.name}</span>
                          <div className="text-xs" style={{ color: '#a3a3a3' }}>{cUser.email}</div>
                          {(cUser.genres || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(cUser.genres || []).slice(0, 2).map((genre, i) => (
                                <span key={i} className="text-xs px-1 py-0.5 rounded" style={{ background: 'rgba(58, 0, 136, 0.2)', color: '#a78bfa' }}>
                                  {genre}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {cUser.membership_tier || 'None'}
                        </span>
                      </td>
                      <td className="p-3" style={{ color: '#ffffff' }}>{cUser.post_count}</td>
                      <td className="p-3" style={{ color: '#ffffff' }}>{cUser.followers}</td>
                      <td className="p-3">
                        {cUser.community_banned ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-500 flex items-center gap-1 w-fit">
                            <ShieldOff size={10} /> Banned
                          </span>
                        ) : (cUser.warning_count || 0) > 0 ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center gap-1 w-fit">
                            <AlertTriangle size={10} /> {cUser.warning_count} warning{cUser.warning_count > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-500 flex items-center gap-1 w-fit">
                            <CheckCircle size={10} /> Good
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {warningUserId === cUser.id ? (
                            <div className="flex items-center gap-2">
                              <Input 
                                placeholder="Reason..." 
                                value={warningReason}
                                onChange={(e) => setWarningReason(e.target.value)}
                                className="w-32 h-7 text-xs"
                              />
                              <Button 
                                size="sm" 
                                onClick={() => handleWarnUser(cUser.id)}
                                style={{ background: '#eab308', color: '#000' }}
                              >
                                <CheckCircle size={12} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => { setWarningUserId(null); setWarningReason(''); }}
                              >
                                <XCircle size={12} />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setWarningUserId(cUser.id)}
                                title="Issue warning"
                                style={{ color: '#eab308', borderColor: '#eab308' }}
                              >
                                <AlertTriangle size={12} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleToggleBan(cUser.id)}
                                title={cUser.community_banned ? 'Unban user' : 'Ban user'}
                                style={{ color: cUser.community_banned ? '#22c55e' : '#ef4444', borderColor: cUser.community_banned ? '#22c55e' : '#ef4444' }}
                              >
                                {cUser.community_banned ? <Shield size={12} /> : <ShieldOff size={12} />}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="announcements">
            <div className="mb-4">
              <Button onClick={() => setShowAnnouncementForm(true)} className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>
                <Plus size={16} className="mr-2" /> Add Announcement
              </Button>
            </div>

            {showAnnouncementForm && (
              <div className="glass-panel rounded-2xl p-6 mb-6 border">
                <h3 className="text-xl font-bold mb-4">Create New Announcement</h3>
                <form onSubmit={handleCreateAnnouncement} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={announcementForm.title} onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea value={announcementForm.content} onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})} required rows={4} />
                  </div>
                  <div>
                    <Label>Tier Required</Label>
                    <select value={announcementForm.tier_required} onChange={(e) => setAnnouncementForm({...announcementForm, tier_required: e.target.value})} className="w-full p-2 rounded-md border border-border bg-background">
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="rounded-full" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>Create</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAnnouncementForm(false)} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="glass-panel rounded-xl p-4 border">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold" style={{ color: '#ffffff' }}>{announcement.title}</h4>
                    <Button size="sm" variant="outline" onClick={() => handleDeleteAnnouncement(announcement.id)} style={{ color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <p className="text-sm mb-2" style={{ color: '#a3a3a3' }}>{announcement.content}</p>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>Tier: {announcement.tier_required}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Name</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Email</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Tier</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Status</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-border/50">
                      <td className="p-4" style={{ color: '#ffffff' }}>{user.name}</td>
                      <td className="p-4" style={{ color: '#a3a3a3' }}>{user.email}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {user.membership_tier || 'None'}
                        </span>
                      </td>
                      <td className="p-4" style={{ color: '#a3a3a3' }}>{user.membership_status}</td>
                      <td className="p-4">
                        <select
                          onChange={(e) => {
                            const [tier, status] = e.target.value.split('|');
                            handleUpdateMembership(user.id, tier, status);
                          }}
                          className="p-2 rounded-md border border-border bg-background text-sm"
                        >
                          <option value="">Change Membership</option>
                          <option value="starter|active">Starter - Active</option>
                          <option value="pro|active">Pro - Active</option>
                          <option value="elite|active">Elite - Active</option>
                          <option value="|inactive">Remove Membership</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="posts">
            <div className="grid grid-cols-1 gap-4">
              {posts.map((post) => (
                <div key={post.id} className="glass-panel rounded-xl p-4 border">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-semibold" style={{ color: '#ffffff' }}>{post.user_name}</span>
                      <span className="text-xs ml-2" style={{ color: '#a3a3a3' }}>{new Date(post.created_at).toLocaleString()}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleDeletePost(post.id)} style={{ color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <p className="text-sm" style={{ color: '#a3a3a3' }}>{post.content}</p>
                  <div className="mt-2 text-xs" style={{ color: '#a3a3a3' }}>
                    Likes: {post.likes} | Comments: {post.comments?.length || 0}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            {/* Payment Analytics Overview */}
            {paymentAnalytics && (
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp size={20} style={{ color: '#D4AF37' }} />
                  Revenue Analytics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="glass-panel rounded-xl p-4 border text-center">
                    <DollarSign className="mx-auto mb-2" size={24} style={{ color: '#22c55e' }} />
                    <div className="text-2xl font-bold" style={{ color: '#22c55e' }}>£{paymentAnalytics.total_revenue?.toFixed(2)}</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>Total Revenue</div>
                  </div>
                  <div className="glass-panel rounded-xl p-4 border text-center">
                    <Calendar className="mx-auto mb-2" size={24} style={{ color: '#D4AF37' }} />
                    <div className="text-2xl font-bold" style={{ color: '#D4AF37' }}>£{paymentAnalytics.monthly_revenue?.toFixed(2)}</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>This Month</div>
                  </div>
                  <div className="glass-panel rounded-xl p-4 border text-center">
                    <RefreshCw className="mx-auto mb-2" size={24} style={{ color: '#3b82f6' }} />
                    <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>£{paymentAnalytics.subscription_revenue?.toFixed(2)}</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>Subscription Revenue</div>
                  </div>
                  <div className="glass-panel rounded-xl p-4 border text-center">
                    <FileText className="mx-auto mb-2" size={24} style={{ color: '#a855f7' }} />
                    <div className="text-2xl font-bold" style={{ color: '#a855f7' }}>£{paymentAnalytics.course_revenue?.toFixed(2)}</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>Course Sales</div>
                  </div>
                </div>

                {/* Active Subscriptions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="glass-panel rounded-xl p-4 border">
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
                      <Users size={16} style={{ color: '#D4AF37' }} />
                      Active Subscriptions by Tier
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Starter</span>
                        <span className="font-bold" style={{ color: '#D4AF37' }}>{paymentAnalytics.active_subscriptions?.starter || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Pro</span>
                        <span className="font-bold" style={{ color: '#D4AF37' }}>{paymentAnalytics.active_subscriptions?.pro || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Elite</span>
                        <span className="font-bold" style={{ color: '#D4AF37' }}>{paymentAnalytics.active_subscriptions?.elite || 0}</span>
                      </div>
                      <div className="border-t border-border pt-2 mt-2 flex justify-between items-center">
                        <span style={{ color: '#ffffff' }}>Total Active</span>
                        <span className="font-bold text-lg" style={{ color: '#22c55e' }}>{paymentAnalytics.active_subscriptions?.total || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="glass-panel rounded-xl p-4 border">
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#ffffff' }}>
                      <AlertCircle size={16} style={{ color: '#ef4444' }} />
                      Payment Status
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Course Purchases</span>
                        <span className="font-bold" style={{ color: '#22c55e' }}>{paymentAnalytics.course_purchases || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Lifetime Members</span>
                        <span className="font-bold" style={{ color: '#a855f7' }}>{paymentAnalytics.lifetime_members || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Failed Payments</span>
                        <span className="font-bold" style={{ color: '#ef4444' }}>{paymentAnalytics.failed_payments || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Pending</span>
                        <span className="font-bold" style={{ color: '#eab308' }}>{paymentAnalytics.pending_payments || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: '#a3a3a3' }}>Refunds</span>
                        <span className="font-bold" style={{ color: '#ef4444' }}>{paymentAnalytics.refunds?.count || 0} (£{paymentAnalytics.refunds?.total_amount?.toFixed(2) || '0.00'})</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-center mb-6" style={{ color: '#666' }}>
                  💡 Note: Bank payouts are managed directly in your Stripe Dashboard. All funds are automatically paid out according to your Stripe payout schedule.
                </p>
              </div>
            )}

            {/* Recent Subscription Events */}
            {subscriptionEvents.length > 0 && (
              <div className="mb-8">
                <h4 className="font-semibold mb-3" style={{ color: '#ffffff' }}>Recent Subscription Events</h4>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {subscriptionEvents.slice(0, 10).map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          event.event_type === 'subscription_created' ? 'bg-green-500/10 text-green-500' :
                          event.event_type === 'payment_failed' ? 'bg-red-500/10 text-red-500' :
                          event.event_type === 'subscription_refunded' ? 'bg-yellow-500/10 text-yellow-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {event.event_type?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm" style={{ color: '#a3a3a3' }}>{event.user_email}</span>
                      </div>
                      <span className="text-xs" style={{ color: '#666' }}>{new Date(event.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <h4 className="font-semibold mb-3" style={{ color: '#ffffff' }}>All Transactions</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Date</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Customer</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Amount</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Status</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Type</th>
                    <th className="text-left p-4" style={{ color: '#ffffff' }}>Package</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-border/50">
                      <td className="p-4" style={{ color: '#a3a3a3' }}>{new Date(payment.created_at).toLocaleDateString()}</td>
                      <td className="p-4">
                        <div>
                          <span style={{ color: '#ffffff' }}>{payment.user_name || 'Unknown'}</span>
                          <div className="text-xs" style={{ color: '#666' }}>{payment.user_email}</div>
                        </div>
                      </td>
                      <td className="p-4" style={{ color: '#ffffff' }}>£{payment.amount?.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          payment.payment_status === 'paid' ? 'bg-green-500/10 text-green-500' : 
                          payment.payment_status === 'failed' ? 'bg-red-500/10 text-red-500' :
                          'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {payment.payment_status}
                          {payment.refunded && ' (Refunded)'}
                        </span>
                      </td>
                      <td className="p-4" style={{ color: '#a3a3a3' }}>{payment.payment_mode === 'subscription' ? 'Subscription' : 'One-time'}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {payment.package_type || payment.metadata?.tier || 'course'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Media Viewer Modal */}
      <MediaViewer
        url={viewerData.url}
        type={viewerData.type}
        title={viewerData.title}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
};

export default Admin;